import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Key, FileText, Search, RefreshCw, X, PlusCircle, MinusCircle, Eye, EyeOff, KeyRound, Copy, RotateCw, Ban, ChevronLeft, Loader2, AlertTriangle } from 'lucide-react';
import { adminApi, externalApiKeyApi, eventsApi, getChatExternalUrl } from '../../services/api';
import { AppUser, UserStatsResponse, SignupFormField, Event, ExternalApiKeyView, ExternalApiKeySecret, ExternalApiKeyAuditEntry, ExternalApiKeyUsage } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const FIELD_TYPES: { value: SignupFormField['fieldType']; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'boolean', label: 'Yes/No' },
];

const emptyUserForm = () => ({ name: '', email: '', password: '', phone: '', isActive: true });
const emptyPwForm = () => ({ newPassword: '', showPassword: false });

// =====================================================================================
// External API key management (Chat External). Admin only. Everything below is the
// "External API" modal opened from a client row; components are kept in this file on purpose.
// =====================================================================================

type ExtTab = 'overview' | 'versions' | 'audit' | 'usage';

const GRACE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'None — revoke the old key immediately' },
  { value: 1, label: '1 hour' },
  { value: 24, label: '24 hours' },
  { value: 72, label: '3 days' },
  { value: 168, label: '7 days' },
];

const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const formatDateTime = (value?: string): string => (value ? new Date(value).toLocaleString() : '–');
const formatUsd = (value: number): string => `$${value < 1 ? value.toFixed(4) : value.toFixed(2)}`;

const KEY_STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-50 text-green-700 border-green-200',
  rotating: 'bg-amber-50 text-amber-700 border-amber-200',
  revoked: 'bg-red-50 text-red-700 border-red-200',
  expired: 'bg-gray-100 text-gray-600 border-gray-200',
  disabled: 'bg-gray-100 text-gray-600 border-gray-300',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${KEY_STATUS_STYLES[status] || KEY_STATUS_STYLES.expired}`}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </span>
);

const Spinner: React.FC<{ label?: string }> = ({ label = 'Loading...' }) => (
  <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400">
    <Loader2 className="w-4 h-4 animate-spin" /> {label}
  </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex flex-col items-center gap-3 py-10 text-center">
    <AlertTriangle className="w-6 h-6 text-red-500" />
    <p className="text-sm text-gray-600">{message}</p>
    <button onClick={onRetry} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Retry</button>
  </div>
);

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/** Confirmation dialog for destructive actions; optionally collects a reason. */
const ConfirmDialog: React.FC<{
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  askReason?: boolean;
  busy?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}> = ({ title, message, confirmLabel, askReason, busy, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 space-y-3">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <div className="text-sm text-gray-600">{message}</div>
          {askReason && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={300} placeholder="Recorded in the audit log" className={inputClass} />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100">
          <button onClick={onCancel} disabled={busy} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">Cancel</button>
          <button onClick={() => onConfirm(reason)} disabled={busy} className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
            {busy ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Shows a full key (masked by default, Show + Copy) with a ready-made curl. Used after create/rotate and for reveal. */
const KeyRevealPanel: React.FC<{ secret: ExternalApiKeySecret; heading: string; keyName?: string; onClose: () => void }> = ({ secret, heading, keyName, onClose }) => {
  const [visible, setVisible] = useState(false);
  const maskedKey = `${secret.key.slice(0, 17)}${'•'.repeat(20)}${secret.key.slice(-4)}`;
  const curl = [
    `curl -X POST ${getChatExternalUrl()} \\`,
    `  -H "X-API-Key: ${secret.key}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"eventId":"<eventId>","message":"What does module 3 cover?"}'`,
  ].join('\n');
  const shownCurl = visible ? curl : curl.replace(secret.key, maskedKey);

  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); }
    catch { toast.error('Copy failed — select the text and copy it manually'); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{heading}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{keyName ? `${keyName} · ` : ''}Version {secret.versionNumber}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Share this key only over a secure channel and store it in a server-side secret manager. You can reveal it again later from the key's Versions tab (each reveal is audited).
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API key</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-xs break-all font-mono">{visible ? secret.key : maskedKey}</code>
              <button onClick={() => setVisible(v => !v)} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} {visible ? 'Hide' : 'Show'}
              </button>
              <button onClick={() => copy(secret.key, 'API key')} className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
          </div>
          {secret.previousVersionGraceEndsAt && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              The previous version keeps working until {formatDateTime(secret.previousVersionGraceEndsAt)}.
            </p>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Example request</label>
              <button onClick={() => copy(curl, 'curl command')} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"><Copy className="w-3 h-3" /> Copy curl</button>
            </div>
            <pre className="px-3 py-2 bg-gray-900 text-gray-100 rounded-md text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all">{shownCurl}</pre>
          </div>
        </div>
        <div className="flex justify-end px-5 py-3 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Done</button>
        </div>
      </div>
    </div>
  );
};

const CreateKeyForm: React.FC<{
  client: AppUser;
  onCancel: () => void;
  onCreated: (secret: ExternalApiKeySecret, name: string) => void;
}> = ({ client, onCancel, onCreated }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [form, setForm] = useState({
    name: '', description: '', environment: 'live' as 'live' | 'test', allEvents: true,
    rateLimitPerMinute: '60', monthlyCostLimitUsd: '', expiresAt: '', exposeUsage: false, allowedIps: '',
  });
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    eventsApi.getAll({ clientId: client._id, limit: 200 })
      .then(res => setEvents(res.data.data || []))
      .catch(() => toast.error('Failed to load this account\'s events'))
      .finally(() => setEventsLoading(false));
  }, [client._id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error('Name must be at least 2 characters'); return; }
    if (!form.allEvents && selectedEvents.length === 0) { toast.error('Select at least one event, or choose All events'); return; }
    const rate = parseInt(form.rateLimitPerMinute, 10);
    if (!Number.isInteger(rate) || rate < 1 || rate > 6000) { toast.error('Rate limit must be between 1 and 6000 per minute'); return; }
    const ips = form.allowedIps.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      const res = await externalApiKeyApi.create({
        clientId: client._id,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        environment: form.environment,
        allowedEventIds: form.allEvents ? [] : selectedEvents,
        expiresAt: form.expiresAt ? new Date(`${form.expiresAt}T23:59:59`).toISOString() : undefined,
        settings: {
          rateLimitPerMinute: rate,
          ...(form.monthlyCostLimitUsd !== '' ? { monthlyCostLimitUsd: Number(form.monthlyCostLimitUsd) } : {}),
          exposeUsage: form.exposeUsage,
          ...(ips.length ? { allowedIps: ips } : {}),
        },
      });
      if (res.data.success && res.data.data) {
        toast.success('API key created');
        onCreated(res.data.data, form.name.trim());
      }
    } catch (error: any) {
      toast.error(apiErrorMessage(error, 'Failed to create key'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme CRM integration" maxLength={80} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
          <select value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value as 'live' | 'test' }))} className={inputClass}>
            <option value="live">live</option>
            <option value="test">test</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">Only changes the key prefix (ebk_live_ / ebk_test_).</p>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={500} placeholder="What this key is used for" className={inputClass} />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Events</label>
        <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
          <input type="checkbox" checked={form.allEvents} onChange={e => setForm(f => ({ ...f, allEvents: e.target.checked }))} className="rounded" />
          All events of this account
        </label>
        {!form.allEvents && (
          <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto divide-y divide-gray-100">
            {eventsLoading ? <Spinner /> : events.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-400">This account has no events yet.</p>
            ) : events.map(ev => (
              <label key={ev._id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <input type="checkbox" className="rounded" checked={selectedEvents.includes(ev._id)}
                  onChange={e => setSelectedEvents(prev => e.target.checked ? [...prev, ev._id] : prev.filter(id => id !== ev._id))} />
                {ev.name}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rate limit (req/min)</label>
          <input type="number" min={1} max={6000} value={form.rateLimitPerMinute} onChange={e => setForm(f => ({ ...f, rateLimitPerMinute: e.target.value }))} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Monthly cost limit (USD)</label>
          <input type="number" min={0} step="0.01" value={form.monthlyCostLimitUsd} onChange={e => setForm(f => ({ ...f, monthlyCostLimitUsd: e.target.value }))} placeholder="Unlimited" className={inputClass} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expires (optional)</label>
          <input type="date" value={form.expiresAt} min={new Date().toISOString().slice(0, 10)} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} className={inputClass} />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">IP allow-list (optional)</label>
        <input value={form.allowedIps} onChange={e => setForm(f => ({ ...f, allowedIps: e.target.value }))} placeholder="203.0.113.5, 198.51.100.7 — empty allows any IP" className={inputClass} />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={form.exposeUsage} onChange={e => setForm(f => ({ ...f, exposeUsage: e.target.checked }))} className="rounded" />
        Return token usage (input/output tokens, never cost) in API responses
      </label>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
        <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{saving ? 'Creating...' : 'Create key'}</button>
      </div>
    </form>
  );
};

const KeyDetail: React.FC<{
  client: AppUser;
  keyId: string;
  onBack: () => void;
  onChanged: () => void;
  onSecret: (secret: ExternalApiKeySecret, heading: string, keyName: string) => void;
}> = ({ client, keyId, onBack, onChanged, onSecret }) => {
  const [keyView, setKeyView] = useState<ExternalApiKeyView | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState<ExtTab>('overview');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | { kind: 'revokeKey' | 'revokeVersion' | 'delete' | 'rotate'; version?: number }>(null);
  const [graceHours, setGraceHours] = useState(24);

  // Editable settings
  const [edit, setEdit] = useState({ name: '', description: '', allEvents: true, rate: '60', cap: '', exposeUsage: false, ips: '' });
  const [editEvents, setEditEvents] = useState<string[]>([]);

  const [audit, setAudit] = useState<{ entries: ExternalApiKeyAuditEntry[]; total: number } | null>(null);
  const [usage, setUsage] = useState<ExternalApiKeyUsage | null>(null);
  const [tabLoading, setTabLoading] = useState(false);
  const [tabError, setTabError] = useState('');

  const applyView = useCallback((view: ExternalApiKeyView) => {
    setKeyView(view);
    setEdit({
      name: view.name, description: view.description || '', allEvents: view.allowedEventIds.length === 0,
      rate: String(view.settings.rateLimitPerMinute), cap: view.settings.monthlyCostLimitUsd !== undefined && view.settings.monthlyCostLimitUsd !== null ? String(view.settings.monthlyCostLimitUsd) : '',
      exposeUsage: view.settings.exposeUsage, ips: (view.settings.allowedIps || []).join(', '),
    });
    setEditEvents(view.allowedEventIds.map(String));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try {
      const [res, evRes] = await Promise.all([
        externalApiKeyApi.get(client._id, keyId),
        eventsApi.getAll({ clientId: client._id, limit: 200 }).catch(() => null),
      ]);
      if (res.data.success && res.data.data) applyView(res.data.data);
      if (evRes) setEvents(evRes.data.data || []);
    } catch (error: any) {
      setLoadError(apiErrorMessage(error, 'Failed to load key'));
    } finally {
      setLoading(false);
    }
  }, [client._id, keyId, applyView]);

  useEffect(() => { load(); }, [load]);

  const loadTab = useCallback(async (which: ExtTab) => {
    if (which !== 'audit' && which !== 'usage') return;
    setTabLoading(true); setTabError('');
    try {
      if (which === 'audit') {
        const res = await externalApiKeyApi.getAudit(client._id, keyId, { limit: 50 });
        setAudit({ entries: res.data.data?.entries || [], total: res.data.data?.pagination?.total || 0 });
      } else {
        const res = await externalApiKeyApi.getUsage(client._id, keyId, 30);
        setUsage(res.data.data || null);
      }
    } catch (error: any) {
      setTabError(apiErrorMessage(error, `Failed to load ${which}`));
    } finally {
      setTabLoading(false);
    }
  }, [client._id, keyId]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const refreshAll = async () => { await load(); onChanged(); if (tab === 'audit' || tab === 'usage') loadTab(tab); };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } catch (error: any) { toast.error(apiErrorMessage(error, 'Action failed')); } finally { setBusy(false); setConfirm(null); }
  };

  const saveSettings = async () => {
    if (!keyView) return;
    const rate = parseInt(edit.rate, 10);
    if (edit.name.trim().length < 2) { toast.error('Name must be at least 2 characters'); return; }
    if (!Number.isInteger(rate) || rate < 1 || rate > 6000) { toast.error('Rate limit must be between 1 and 6000 per minute'); return; }
    if (!edit.allEvents && editEvents.length === 0) { toast.error('Select at least one event, or choose All events'); return; }
    const ips = edit.ips.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
    await run(async () => {
      const res = await externalApiKeyApi.update(client._id, keyView._id, {
        name: edit.name.trim(), description: edit.description.trim(),
        allowedEventIds: edit.allEvents ? [] : editEvents,
        settings: { rateLimitPerMinute: rate, monthlyCostLimitUsd: edit.cap === '' ? null : Number(edit.cap), exposeUsage: edit.exposeUsage, allowedIps: ips },
      });
      if (res.data.success && res.data.data) { applyView(res.data.data); onChanged(); toast.success('Settings saved'); }
    });
  };

  const toggleActive = async () => {
    if (!keyView) return;
    await run(async () => {
      const res = await externalApiKeyApi.update(client._id, keyView._id, { isActive: !keyView.isActive });
      if (res.data.success && res.data.data) { applyView(res.data.data); onChanged(); toast.success(keyView.isActive ? 'Key disabled' : 'Key enabled'); }
    });
  };

  const reveal = async (versionNumber: number) => {
    if (!keyView) return;
    setBusy(true);
    try {
      const res = await externalApiKeyApi.reveal(client._id, keyView._id, versionNumber);
      if (res.data.success && res.data.data) onSecret(res.data.data, 'API key', keyView.name);
      if (tab === 'audit') loadTab('audit');
    } catch (error: any) {
      toast.error(apiErrorMessage(error, 'Failed to reveal key'));
    } finally {
      setBusy(false);
    }
  };

  const doConfirmed = async (reason: string) => {
    if (!keyView || !confirm) return;
    await run(async () => {
      if (confirm.kind === 'rotate') {
        const res = await externalApiKeyApi.rotate(client._id, keyView._id, { gracePeriodHours: graceHours });
        if (res.data.success && res.data.data) { onSecret(res.data.data, 'Key rotated', keyView.name); toast.success('Key rotated'); }
      } else if (confirm.kind === 'revokeKey') {
        await externalApiKeyApi.revokeKey(client._id, keyView._id, reason);
        toast.success('Key revoked');
      } else if (confirm.kind === 'revokeVersion' && confirm.version) {
        await externalApiKeyApi.revokeVersion(client._id, keyView._id, confirm.version, reason);
        toast.success(`Version ${confirm.version} revoked`);
      } else if (confirm.kind === 'delete') {
        await externalApiKeyApi.remove(client._id, keyView._id);
        toast.success('Key deleted');
        onChanged();
        onBack();
        return;
      }
      await refreshAll();
    });
  };

  if (loading) return <Spinner />;
  if (loadError || !keyView) return <ErrorState message={loadError || 'Key not found'} onRetry={load} />;

  const terminal = keyView.status === 'revoked';
  const tabs: { id: ExtTab; label: string }[] = [
    { id: 'overview', label: 'Settings' }, { id: 'versions', label: `Versions (${keyView.versions.length})` },
    { id: 'audit', label: 'Audit log' }, { id: 'usage', label: 'Usage' },
  ];
  const maxDayCost = usage ? Math.max(0.0001, ...usage.byDay.map(d => d.costUsd)) : 1;

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"><ChevronLeft className="w-4 h-4" /> All keys</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{keyView.name}</h3>
            <StatusBadge status={keyView.status} />
            <span className="text-xs text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">{keyView.environment}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1 font-mono">{keyView.maskedKey} · current version {keyView.currentVersion}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!terminal && (
            <>
              <button onClick={() => reveal(keyView.currentVersion)} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"><Eye className="w-4 h-4" /> Reveal key</button>
              <button onClick={() => setConfirm({ kind: 'rotate' })} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-blue-200 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-50"><RotateCw className="w-4 h-4" /> Rotate</button>
              <button onClick={toggleActive} disabled={busy} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">{keyView.isActive ? 'Disable' : 'Enable'}</button>
              <button onClick={() => setConfirm({ kind: 'revokeKey' })} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"><Ban className="w-4 h-4" /> Revoke key</button>
            </>
          )}
          <button onClick={() => setConfirm({ kind: 'delete' })} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-red-200 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"><Trash2 className="w-4 h-4" /> Delete</button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.id ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input value={edit.name} onChange={e => setEdit(s => ({ ...s, name: e.target.value }))} disabled={terminal} maxLength={80} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input value={edit.description} onChange={e => setEdit(s => ({ ...s, description: e.target.value }))} disabled={terminal} maxLength={500} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Events</label>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={edit.allEvents} disabled={terminal} onChange={e => setEdit(s => ({ ...s, allEvents: e.target.checked }))} className="rounded" />
              All events of this account
            </label>
            {!edit.allEvents && (
              <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto divide-y divide-gray-100">
                {events.length === 0 ? <p className="px-3 py-3 text-sm text-gray-400">No events found for this account.</p> : events.map(ev => (
                  <label key={ev._id} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <input type="checkbox" className="rounded" disabled={terminal} checked={editEvents.includes(ev._id)}
                      onChange={e => setEditEvents(prev => e.target.checked ? [...prev, ev._id] : prev.filter(id => id !== ev._id))} />
                    {ev.name}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate limit (req/min)</label>
              <input type="number" min={1} max={6000} value={edit.rate} disabled={terminal} onChange={e => setEdit(s => ({ ...s, rate: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly cost limit (USD)</label>
              <input type="number" min={0} step="0.01" value={edit.cap} disabled={terminal} onChange={e => setEdit(s => ({ ...s, cap: e.target.value }))} placeholder="Unlimited" className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IP allow-list</label>
              <input value={edit.ips} disabled={terminal} onChange={e => setEdit(s => ({ ...s, ips: e.target.value }))} placeholder="Any IP" className={inputClass} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={edit.exposeUsage} disabled={terminal} onChange={e => setEdit(s => ({ ...s, exposeUsage: e.target.checked }))} className="rounded" />
            Return token usage (never cost) in API responses
          </label>
          <p className="text-xs text-gray-400">Editing settings never changes the secret or the version number. To change the secret, rotate the key.</p>
          {!terminal && (
            <div className="flex justify-end">
              <button onClick={saveSettings} disabled={busy} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">Save settings</button>
            </div>
          )}
        </div>
      )}

      {tab === 'versions' && (
        <ol className="space-y-3">
          {[...keyView.versions].sort((a, b) => b.versionNumber - a.versionNumber).map(v => (
            <li key={v.versionNumber} className="border border-gray-200 rounded-lg p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">Version {v.versionNumber}</span>
                  <StatusBadge status={v.status} />
                  {v.versionNumber === keyView.currentVersion && <span className="text-xs text-blue-600">current</span>}
                </div>
                {v.status !== 'revoked' && v.status !== 'expired' && (
                  <div className="flex gap-2">
                    <button onClick={() => reveal(v.versionNumber)} disabled={busy} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"><Eye className="w-3 h-3" /> Reveal key</button>
                    <button onClick={() => setConfirm({ kind: 'revokeVersion', version: v.versionNumber })} disabled={busy} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50"><Ban className="w-3 h-3" /> Revoke</button>
                  </div>
                )}
              </div>
              <dl className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-600">
                <div><dt className="text-gray-400">Key</dt><dd className="font-mono">{v.maskedKey}</dd></div>
                <div><dt className="text-gray-400">Created</dt><dd>{formatDateTime(v.createdAt)}</dd></div>
                <div><dt className="text-gray-400">Last used</dt><dd>{formatDateTime(v.lastUsedAt)}{v.lastUsedIp ? ` · ${v.lastUsedIp}` : ''}</dd></div>
                <div><dt className="text-gray-400">Requests</dt><dd>{v.useCount.toLocaleString()}</dd></div>
                <div><dt className="text-gray-400">Expires</dt><dd>{formatDateTime(v.expiresAt)}</dd></div>
                {v.graceEndsAt && <div><dt className="text-gray-400">Grace ends</dt><dd>{formatDateTime(v.graceEndsAt)}</dd></div>}
                {v.revokedAt && <div><dt className="text-gray-400">Revoked</dt><dd>{formatDateTime(v.revokedAt)}{v.revokeReason ? ` — ${v.revokeReason}` : ''}</dd></div>}
              </dl>
            </li>
          ))}
        </ol>
      )}

      {tab === 'audit' && (
        tabLoading ? <Spinner /> : tabError ? <ErrorState message={tabError} onRetry={() => loadTab('audit')} /> :
        !audit || audit.entries.length === 0 ? <p className="py-8 text-center text-sm text-gray-400">No audit entries yet.</p> : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>{['When', 'Action', 'Version', 'By', 'IP', 'Details'].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {audit.entries.map((a, i) => (
                  <tr key={i} className="align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">{formatDateTime(a.at)}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{a.action.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-gray-600">{a.versionNumber ?? '–'}</td>
                    <td className="px-3 py-2 text-gray-600">{a.actorEmail || '–'}</td>
                    <td className="px-3 py-2 text-gray-600">{a.ip || '–'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 font-mono break-all">{a.changes ? JSON.stringify(a.changes) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {audit.total > audit.entries.length && <p className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100">Showing the latest {audit.entries.length} of {audit.total} entries.</p>}
          </div>
        )
      )}

      {tab === 'usage' && (
        tabLoading ? <Spinner /> : tabError ? <ErrorState message={tabError} onRetry={() => loadTab('usage')} /> :
        !usage ? null : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Requests (30d)', value: usage.totals.requests.toLocaleString() },
                { label: 'Input tokens', value: usage.totals.inputTokens.toLocaleString() },
                { label: 'Output tokens', value: usage.totals.outputTokens.toLocaleString() },
                { label: 'Cost (30d)', value: formatUsd(usage.totals.costUsd) },
              ].map(c => (
                <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-500">{c.label}</p>
                  <p className="text-lg font-semibold text-gray-900">{c.value}</p>
                </div>
              ))}
            </div>
            {usage.byDay.length === 0 ? <p className="py-6 text-center text-sm text-gray-400">No usage recorded in the last 30 days.</p> : (
              <>
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Daily requests and cost</h4>
                  <div className="flex items-end gap-1 h-32 border-b border-gray-200">
                    {usage.byDay.map(d => (
                      <div key={d.date} className="flex-1 min-w-[6px] bg-blue-500/80 hover:bg-blue-600 rounded-t" style={{ height: `${Math.max(4, (d.costUsd / maxDayCost) * 100)}%` }}
                        title={`${d.date}: ${d.requests} requests, ${formatUsd(d.costUsd)}`} />
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>{usage.byDay[0].date}</span><span>{usage.byDay[usage.byDay.length - 1].date}</span></div>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200"><tr>{['Event', 'Requests', 'Tokens in/out', 'Cost'].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>)}</tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {usage.byEvent.map(e => (
                        <tr key={e.eventId}><td className="px-3 py-2 text-gray-900">{e.eventName || e.eventId}</td><td className="px-3 py-2 text-gray-600">{e.requests.toLocaleString()}</td><td className="px-3 py-2 text-gray-600">{e.inputTokens.toLocaleString()} / {e.outputTokens.toLocaleString()}</td><td className="px-3 py-2 text-gray-600">{formatUsd(e.costUsd)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )
      )}

      {confirm?.kind === 'rotate' && (
        <ConfirmDialog
          title="Rotate key" confirmLabel="Rotate key" busy={busy} onCancel={() => setConfirm(null)} onConfirm={doConfirmed}
          message={(
            <div className="space-y-3">
              <p>A new secret (version {Math.max(...keyView.versions.map(v => v.versionNumber)) + 1}) is issued. Choose how long the current key keeps working so the integrator can switch without downtime.</p>
              <select value={graceHours} onChange={e => setGraceHours(Number(e.target.value))} className={inputClass}>
                {GRACE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
        />
      )}
      {confirm?.kind === 'revokeKey' && (
        <ConfirmDialog title="Revoke this key?" confirmLabel="Revoke key" askReason busy={busy} onCancel={() => setConfirm(null)} onConfirm={doConfirmed}
          message="Every version stops working immediately and the key cannot be re-enabled. Create a new key to restore access." />
      )}
      {confirm?.kind === 'revokeVersion' && (
        <ConfirmDialog title={`Revoke version ${confirm.version}?`} confirmLabel="Revoke version" askReason busy={busy} onCancel={() => setConfirm(null)} onConfirm={doConfirmed}
          message="This version stops working immediately. If it is the only active version, the key is unusable until you rotate it." />
      )}
      {confirm?.kind === 'delete' && (
        <ConfirmDialog title="Delete this key?" confirmLabel="Delete key" busy={busy} onCancel={() => setConfirm(null)} onConfirm={doConfirmed}
          message="Only keys that were never used, or are fully revoked, can be deleted. The key and its audit log are removed permanently." />
      )}
    </div>
  );
};

const ExternalApiModal: React.FC<{ client: AppUser; onClose: () => void }> = ({ client, onClose }) => {
  const [keys, setKeys] = useState<ExternalApiKeyView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ secret: ExternalApiKeySecret; heading: string; keyName?: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await externalApiKeyApi.listForClient(client._id);
      setKeys(res.data.data?.keys || []);
    } catch (e: any) {
      setError(apiErrorMessage(e, 'Failed to load API keys'));
    } finally {
      setLoading(false);
    }
  }, [client._id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-label={`External API keys for ${client.name}`}>
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg">
          <div>
            <h2 className="text-base font-semibold text-gray-900">External API — {client.name}</h2>
            <p className="text-xs text-gray-500">{client.email}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          {view === 'list' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">API keys let this account's backend call <code className="text-xs bg-gray-100 px-1 rounded">POST /api/chat/external</code>.</p>
                <button onClick={() => setView('create')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"><Plus className="w-4 h-4" /> Create key</button>
              </div>
              {loading ? <Spinner /> : error ? <ErrorState message={error} onRetry={load} /> : keys.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-lg py-12 text-center">
                  <KeyRound className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No API keys yet.</p>
                  <p className="text-xs text-gray-400">Create a key to give this account's integrators access.</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>{['Name', 'Status', 'Version', 'Key', 'Last used', '30-day requests / cost', ''].map(h => <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {keys.map(k => (
                        <tr key={k._id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium text-gray-900">{k.name}<div className="text-xs text-gray-400 font-normal">{k.allowedEventIds.length ? `${k.allowedEventIds.length} event(s)` : 'All events'}</div></td>
                          <td className="px-3 py-2"><StatusBadge status={k.status} /></td>
                          <td className="px-3 py-2 text-gray-600">v{k.currentVersion}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-600 whitespace-nowrap">{k.maskedKey}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDateTime(k.lastUsedAt)}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{k.usage30d.requests.toLocaleString()} / {formatUsd(k.usage30d.costUsd)}</td>
                          <td className="px-3 py-2 text-right"><button onClick={() => { setSelectedKeyId(k._id); setView('detail'); }} className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50">Manage</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {view === 'create' && (
            <div className="space-y-4">
              <button onClick={() => setView('list')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"><ChevronLeft className="w-4 h-4" /> All keys</button>
              <h3 className="text-base font-semibold text-gray-900">Create API key</h3>
              <CreateKeyForm client={client} onCancel={() => setView('list')}
                onCreated={(secret, name) => { setRevealed({ secret, heading: 'API key created', keyName: name }); setView('list'); load(); }} />
            </div>
          )}

          {view === 'detail' && selectedKeyId && (
            <KeyDetail client={client} keyId={selectedKeyId} onBack={() => { setView('list'); load(); }} onChanged={load}
              onSecret={(secret, heading, keyName) => setRevealed({ secret, heading, keyName })} />
          )}
        </div>
      </div>
      {revealed && <KeyRevealPanel secret={revealed.secret} heading={revealed.heading} keyName={revealed.keyName} onClose={() => setRevealed(null)} />}
    </div>
  );
};

const AppUsers: React.FC = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(false);

  // Create/edit user modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [userForm, setUserForm] = useState(emptyUserForm());

  // Reset password modal
  const [resetPwVisible, setResetPwVisible] = useState(false);
  const [resetPwUser, setResetPwUser] = useState<AppUser | null>(null);
  const [pwForm, setPwForm] = useState(emptyPwForm());

  // External API modal (admin only)
  const [externalApiUser, setExternalApiUser] = useState<AppUser | null>(null);

  // Signup form modal
  const [signupFormUser, setSignupFormUser] = useState<AppUser | null>(null);
  const [signupFormVisible, setSignupFormVisible] = useState(false);
  const [signupFormFields, setSignupFormFields] = useState<SignupFormField[]>([]);
  const [dropdownOptionsDraft, setDropdownOptionsDraft] = useState<string[]>([]);
  const [signupFormLoading, setSignupFormLoading] = useState(false);
  const [signupFormSaving, setSignupFormSaving] = useState(false);

  const [stats, setStats] = useState<UserStatsResponse | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({ role: '' as '' | 'admin' | 'client', isActive: '' as '' | 'true' | 'false', search: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAdmin) { fetchUsers(); fetchStats(); }
  }, [pagination.current, pagination.pageSize, filters, isAdmin]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params: any = { page: pagination.current, limit: pagination.pageSize };
      if (filters.role) params.role = filters.role;
      if (filters.isActive !== '') params.isActive = filters.isActive === 'true';
      if (filters.search) params.search = filters.search;
      const response = await adminApi.getAllUsers(params);
      if (response.data.success && response.data.data) {
        setUsers(response.data.data.users);
        setPagination(p => ({ ...p, total: response.data.data!.pagination.total }));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      if (response.data.success && response.data.data) setStats(response.data.data);
    } catch {}
  };

  const openCreate = () => {
    setEditingUser(null);
    setUserForm(emptyUserForm());
    setModalVisible(true);
  };

  const openEdit = (user: AppUser) => {
    setEditingUser(user);
    setUserForm({ name: user.name, email: user.email, password: '', phone: user.phone || '', isActive: user.isActive });
    setModalVisible(true);
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userForm.name || !userForm.email) { toast.error('Name and email are required'); return; }
    if (!editingUser && !userForm.password) { toast.error('Password is required'); return; }
    try {
      if (editingUser) {
        const payload: any = { name: userForm.name, email: userForm.email, phone: userForm.phone, isActive: userForm.isActive };
        const response = await adminApi.updateUser(editingUser._id, payload);
        if (response.data.success) { toast.success('User updated successfully'); setModalVisible(false); fetchUsers(); }
      } else {
        const payload: any = { name: userForm.name, email: userForm.email, password: userForm.password, phone: userForm.phone };
        const response = await adminApi.createCompanyUser(payload);
        if (response.data.success) { toast.success('User created successfully'); setModalVisible(false); fetchUsers(); fetchStats(); }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await adminApi.deleteUser(id);
      if (response.data.success) { toast.success('User deleted successfully'); fetchUsers(); fetchStats(); }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (user: AppUser) => {
    try {
      const response = await adminApi.toggleUserStatus(user._id);
      if (response.data.success) {
        toast.success(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
        fetchUsers(); fetchStats();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to toggle user status');
    }
  };

  const openResetPw = (user: AppUser) => {
    setResetPwUser(user);
    setPwForm(emptyPwForm());
    setResetPwVisible(true);
  };

  const handleResetPwSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwForm.newPassword || pwForm.newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (!resetPwUser) return;
    try {
      const response = await adminApi.resetUserPassword(resetPwUser._id, { newPassword: pwForm.newPassword });
      if (response.data.success) { toast.success('Password reset successfully'); setResetPwVisible(false); }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const openSignupForm = async (user: AppUser) => {
    if (user.role !== 'client') return;
    setSignupFormUser(user);
    setSignupFormVisible(true);
    setSignupFormLoading(true);
    try {
      const response = await adminApi.getSignupForm(user._id);
      if (response.data.success && response.data.data?.signupFormFields) {
        const sorted = [...response.data.data.signupFormFields].sort((a, b) => a.order - b.order);
        setSignupFormFields(sorted);
        setDropdownOptionsDraft(sorted.map(f => (f.fieldType === 'dropdown' && Array.isArray(f.options) ? f.options.join(', ') : '')));
      } else {
        setSignupFormFields([]); setDropdownOptionsDraft([]);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load signup form');
      setSignupFormFields([]); setDropdownOptionsDraft([]);
    } finally {
      setSignupFormLoading(false);
    }
  };

  const handleSignupFormSave = async () => {
    if (!signupFormUser) return;
    const withOrder = signupFormFields.map((f, i) => {
      const base = { ...f, order: i };
      if (f.fieldType === 'dropdown') {
        const raw = dropdownOptionsDraft[i] ?? '';
        base.options = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : undefined;
      }
      return base;
    });
    if (!withOrder.every(f => f.fieldName?.trim() && f.fieldCode?.trim())) {
      toast.error('Each field must have a label and field code'); return;
    }
    setSignupFormSaving(true);
    try {
      const response = await adminApi.updateSignupForm(signupFormUser._id, { signupFormFields: withOrder });
      if (response.data.success) { toast.success('Signup form saved'); setSignupFormVisible(false); }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save signup form');
    } finally {
      setSignupFormSaving(false);
    }
  };

  const changeSignupField = (index: number, key: keyof SignupFormField, value: any) => {
    setSignupFormFields(prev => {
      const next = [...prev];
      (next[index] as any)[key] = value;
      if (key === 'fieldName' && typeof value === 'string') {
        next[index].fieldCode = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || next[index].fieldCode;
      }
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));

  if (!isAdmin) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500">You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">Client Management</h1>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats.totalUsers, color: 'text-gray-900' },
            { label: 'Active Users', value: stats.activeUsers, color: 'text-green-700' },
            { label: 'Admin Users', value: stats.adminUsers, color: 'text-red-700' },
            { label: 'Client Users', value: stats.companyUsers, color: 'text-blue-700' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={filters.search} onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPagination(p => ({ ...p, current: 1 })); }} placeholder="Search by name or email" className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-60" />
          </div>
          <select value={filters.role} onChange={e => { setFilters(f => ({ ...f, role: e.target.value as any })); setPagination(p => ({ ...p, current: 1 })); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="client">Client</option>
          </select>
          <select value={filters.isActive} onChange={e => { setFilters(f => ({ ...f, isActive: e.target.value as any })); setPagination(p => ({ ...p, current: 1 })); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button onClick={() => { setFilters({ role: '', isActive: '', search: '' }); setPagination(p => ({ ...p, current: 1 })); fetchUsers(); fetchStats(); }} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-medium text-gray-700">Users</h2>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Create Client User
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Name', 'Email', 'Phone', 'Role', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No users found</td></tr>
              ) : users.map(user => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{user.name}</td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3 text-gray-600">{user.phone || '–'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${user.role === 'admin' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                      {user.role.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${user.isActive ? 'text-green-700' : 'text-gray-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-gray-300'}`} />
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center flex-wrap gap-1">
                      <button onClick={() => openEdit(user)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"><Pencil className="w-3 h-3" /> Edit</button>
                      <button onClick={() => openResetPw(user)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"><Key className="w-3 h-3" /> Reset Pw</button>
                      {user.role === 'client' && (
                        <button onClick={() => openSignupForm(user)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"><FileText className="w-3 h-3" /> Form</button>
                      )}
                      {user.role === 'client' && (
                        <button onClick={() => setExternalApiUser(user)} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"><KeyRound className="w-3 h-3" /> External API</button>
                      )}
                      <button onClick={() => handleToggleStatus(user)} className={`px-2 py-1 text-xs border rounded ${user.isActive ? 'border-orange-200 text-orange-700 hover:bg-orange-50' : 'border-green-200 text-green-700 hover:bg-green-50'}`}>
                        {user.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => handleDelete(user._id)} disabled={user.role === 'admin'} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="w-3 h-3" /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">Total: {pagination.total} users</p>
            <div className="flex items-center gap-2">
              <button disabled={pagination.current <= 1 || loading} onClick={() => setPagination(p => ({ ...p, current: p.current - 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Prev</button>
              <span className="text-xs text-gray-600">Page {pagination.current} of {totalPages}</span>
              <button disabled={pagination.current >= totalPages || loading} onClick={() => setPagination(p => ({ ...p, current: p.current + 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      {modalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">{editingUser ? 'Edit User' : 'Create Client User'}</h2>
              <button onClick={() => setModalVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUserSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input value={userForm.name} onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} placeholder="Full Name" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                  <input type="password" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} placeholder="Password" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                <input value={userForm.phone} onChange={e => setUserForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1234567890" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {editingUser && (
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <button type="button" onClick={() => setUserForm(f => ({ ...f, isActive: !f.isActive }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userForm.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userForm.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-sm text-gray-600">{userForm.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setModalVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">{editingUser ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Reset Password for {resetPwUser?.name}</h2>
              <button onClick={() => setResetPwVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleResetPwSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type={pwForm.showPassword ? 'text' : 'password'} value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="New Password" className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button type="button" onClick={() => setPwForm(f => ({ ...f, showPassword: !f.showPassword }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {pwForm.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setResetPwVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Reset Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* External API keys modal */}
      {externalApiUser && <ExternalApiModal client={externalApiUser} onClose={() => setExternalApiUser(null)} />}

      {/* Signup Form Config Modal */}
      {signupFormVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Signup Form — {signupFormUser?.name || 'Client'}</h2>
              <button onClick={() => setSignupFormVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-500 mb-4">These fields will appear on the event landing signup form for this client. Order is top to bottom.</p>
              {signupFormLoading ? (
                <div className="py-8 text-center text-gray-400">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {signupFormFields.map((field, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <input placeholder="Label" value={field.fieldName} onChange={e => changeSignupField(index, 'fieldName', e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <input placeholder="Field code" value={field.fieldCode} onChange={e => changeSignupField(index, 'fieldCode', e.target.value)} className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <select value={field.fieldType} onChange={e => changeSignupField(index, 'fieldType', e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                          <input type="checkbox" checked={field.isRequired} onChange={e => changeSignupField(index, 'isRequired', e.target.checked)} className="rounded" />
                          Req
                        </label>
                        <button type="button" onClick={() => {
                          setSignupFormFields(prev => prev.filter((_, i) => i !== index).map((f, i) => ({ ...f, order: i })));
                          setDropdownOptionsDraft(prev => prev.filter((_, i) => i !== index));
                        }} className="text-red-500 hover:text-red-700 p-1">
                          <MinusCircle className="w-4 h-4" />
                        </button>
                      </div>
                      {field.fieldType === 'dropdown' && (
                        <input placeholder="Options (comma-separated, e.g. Option A, Option B)" value={dropdownOptionsDraft[index] ?? ''} onChange={e => {
                          const next = [...dropdownOptionsDraft];
                          if (next.length <= index) next.length = index + 1;
                          next[index] = e.target.value;
                          setDropdownOptionsDraft(next);
                        }} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => {
                    setSignupFormFields(prev => [...prev, { fieldName: '', fieldCode: `field_${Date.now()}`, fieldType: 'text', isRequired: false, order: prev.length }]);
                    setDropdownOptionsDraft(prev => [...prev, '']);
                  }} className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600">
                    <PlusCircle className="w-4 h-4" /> Add field
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
              <button onClick={() => setSignupFormVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
              <button onClick={handleSignupFormSave} disabled={signupFormSaving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                {signupFormSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppUsers;
